// File created by Reginald Sahil Chand on 26-03-2024.

import React, {useState, type ReactElement, type MouseEventHandler} from 'react';

import {Header} from '../components/layouts/widgets/header/Header';
import {Tasks} from '../components/layouts/widgets/tasks/Tasks';
import {TaskNavigation} from '../components/layouts/widgets/navigation/TaskNavigation';
import {taskNavigationData} from '../data/taskNavigationData';
import {Footer} from '../components/layouts/widgets/footer/Footer';

/**
 * @author Reginald Sahil Chand
 * @email Developer.ReginaldSahilChand@gmail.com
 *
 * @description This component renders the following listed components:
 * 				[1] Header component.
 * 				[2] Tasks component. (Widget to display task information such as pending, completed etc.)
 * 				[3] TaskNavigation component.
 * 				[4] Footer component.
 * @version 0.1.0
 *
 * @returns {JSX Element} TaskManagementSystem component.
 */
export const TaskManagementSystem: React.FC = React.memo((): ReactElement => {
	const [taskNavigationDrawerState, setTaskNavigationDrawerState] = useState<boolean>(false);
	const [activeIndexState, setActiveIndexState] = useState<number>(1);

	/**
	 * @author Reginald Sahil Chand
	 * @email Developer.ReginaldSahilChand@gmail.com
	 *
	 * @description This function is used to update the taskNavigationDrawerState.
	 * 				On every click, when the function executes, the previous state gets updated with the current state.
	 * 				The updated state will be the opposite of the previous state in relation to a boolean value.
	 * 				The taskNavigationDrawerState will then be used to toggle the drawer in relation to opening and closing.
	 * @version 0.1.0
	 */
	const handleTaskNavigationDrawer: () => void = () => {
		try {
			setTaskNavigationDrawerState((prev) => !prev);
		} catch (error: unknown) {
			console.log('An error has occurred. Refer: ', error);
		}
	};

	/**
	 * @author Reginald Sahil Chand
	 * @email Developer.ReginaldSahilChand@gmail.com
	 *
	 * @description This function retrieves the event.currentTarget.textContent based on the user,s onclick element,
	 *              compares it with taskNavigationData[i]?.title and sets the setActiveIndex(i) as required using a for loop,
	 *              which simply updates the activeIndex State based on the clicked text-content.
	 * @version 0.1.0
	 *
	 * @param  event - event.currentTarget.textContent
	 *         (This parameter is used to compare textContent with the Array[i].title)
	 */
	const handleTaskNavigationLink: MouseEventHandler<HTMLDivElement> = (event) => {
		/**
		 * @author Reginald Sahil Chand
		 * @email Developer.ReginaldSahilChand@gmail.com
		 *
		 * TODO: If the array becomes large, consider using array.map and update the activeIndex state to taskId,
		 * TODO: Then update the variable activeIndex to taskId where used.
		 * TODO: Then if not already defined, define ID as taskId: 1 and so on in the arrays each item you will map from
		 * TODO: In this case its taskNavigationData. Then update the function to set mapped arrays id to setTaskId(id).
		 * TODO: The where you want to apply logic based on the elements click or id, you compare the
		 * TODO: Array.map(mappedElement.id)
		 * TODO: With taskId. This approach will tend to be more efficient when dealing with a large number or array data.
		 */

		try {
			for (let i = 0; i < taskNavigationData?.length; i++) {
				if (event?.currentTarget.textContent === taskNavigationData[i]?.title) {
					setActiveIndexState(i);
				}
			}
		} catch (error: unknown) {
			console.log('An error has occurred. Refer: ', error);
		}
	};

	return (
		<section className="w-full min-h-screen relative grid grid-rows-[6rem,calc(100vh-12rem),6rem] grid-cols-1 overflow-y-auto md:grid-rows-[6rem,calc(100vh-12rem),6rem] md:grid-cols-[1fr,calc(100%-18.75rem)]">
			<Header
				taskNavigationDrawerState={taskNavigationDrawerState}
				taskNavigationDrawerFunction={handleTaskNavigationDrawer}
			/>
			<TaskNavigation
				taskNavigationData={taskNavigationData}
				taskNavigationDrawerState={taskNavigationDrawerState}
				taskNavigationDrawerFunction={handleTaskNavigationDrawer}
				activeIndexState={activeIndexState}
				taskNavigationLinkFunction={handleTaskNavigationLink}
			/>
			<Tasks activeIndexState={activeIndexState} />
			<Footer />
		</section>
	);
});

TaskManagementSystem.displayName = 'TaskManagementSystem';
